/**
 * Integrator wiring: calls optional hooks exported by builder modules when they exist, so modules
 * integrate the moment they land without editing main.ts. Each hook is guarded — a module that has
 * not yet delivered the export is simply skipped.
 */
import type Phaser from 'phaser';
import * as maps from '@/maps';
import * as characters from '@/characters';
import * as dialogue from '@/dialogue';
import * as cinematics from '@/cinematics';
import * as ui from '@/ui';
import * as audio from '@/audio';
import * as effects from '@/effects';
import * as combat from '@/combat';
import * as tools from '@/tools';
import * as save from '@/save/autosave';
import * as world from '@/world';

type Hooks = Record<string, unknown>;
const MODULES: Record<string, Hooks> = { maps, characters, dialogue, cinematics, ui, audio, effects, combat, tools, world };

function call<T>(mod: Hooks, name: string, ...args: unknown[]): T | undefined {
  const fn = mod[name];
  if (typeof fn === 'function') {
    try { return (fn as (...a: unknown[]) => T)(...args); } catch (e) { console.error(`[wiring] ${name} failed`, e); }
  }
  return undefined;
}

/** Register every module's showcase entries (modules export registerShowcase()). */
export function registerAllShowcases(): void {
  for (const [key, mod] of Object.entries(MODULES)) call(mod, 'registerShowcase', key);
}

/** UI builder installs its components on UIScene through attachUi(scene). */
export function attachUiIfAvailable(scene: Phaser.Scene): boolean {
  return call<unknown>(ui as Hooks, 'attachUi', scene) !== undefined || typeof (ui as Hooks)['attachUi'] === 'function';
}

/** UI builder's ensureFonts(): resolves when the bundled CJK font is ready (or immediately if absent). */
export async function ensureFontsIfAvailable(): Promise<void> {
  const p = call<Promise<void>>(ui as Hooks, 'ensureFonts');
  if (p) { try { await Promise.race([p, new Promise<void>((r) => setTimeout(r, 4000))]); } catch (e) { console.warn('[wiring] ensureFonts failed', e); } }
}

/** UI builder's renderTitle(scene, opts): returns true when it handled the title visuals. */
export function renderTitleIfAvailable(scene: Phaser.Scene, opts: unknown): boolean {
  if (typeof (ui as Hooks)['renderTitle'] !== 'function') return false;
  call(ui as Hooks, 'renderTitle', scene, opts);
  return true;
}

export function renderChapterEndIfAvailable(scene: Phaser.Scene, opts: unknown): boolean {
  if (typeof (ui as Hooks)['renderChapterEnd'] !== 'function') return false;
  call(ui as Hooks, 'renderChapterEnd', scene, opts);
  return true;
}

/** Save builder's installAutosaveTriggers(ctx): autosave on scene enter. */
export function installAutosaveIfAvailable(ctx: unknown): void { call(save as Hooks, 'installAutosaveTriggers', ctx); }

/** Audio builder's ensureAudioDirector(game): creates the director explicitly instead of polling. */
export function ensureAudioIfAvailable(game: Phaser.Game): void { call(audio as Hooks, 'ensureAudioDirector', game); }
